/**
 * API Client for Live Chat Widget
 */

import type { InitChatRequest, SendMessageRequest, VisitorSession, Message } from '../types';

export class WidgetAPI {
  private apiUrl: string;
  private token: string | null = null;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl.replace(/\/$/, ''); // Remove trailing slash
  }

  setToken(token: string) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[Widget API] Request failed:', error);
      throw error;
    }
  }

  async initChat(data: InitChatRequest): Promise<VisitorSession> {
    return this.request<VisitorSession>('/api/widget/init-chat/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendMessage(data: SendMessageRequest): Promise<{ message_id: string; timestamp: string }> {
    return this.request('/api/widget/send-message/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMessages(conversationId: number, after?: string): Promise<{ messages: Message[]; has_more: boolean }> {
    const params = new URLSearchParams({
      conversation_id: conversationId.toString(),
      ...(after && { after }),
    });
    return this.request(`/api/widget/messages/?${params}`);
  }

  async sendTypingIndicator(conversationId: number, isTyping: boolean): Promise<void> {
    await this.request('/api/widget/typing/', {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: conversationId,
        is_typing: isTyping,
      }),
    });
  }

  async getSessionInfo(): Promise<VisitorSession> {
    return this.request('/api/widget/session/');
  }

  async getOnlineStatus(): Promise<{ online: boolean; agent_count: number; message: string }> {
    return this.request('/api/widget/online-status/');
  }

  async getAgentInfo(): Promise<{ agent_name: string | null; agent_online: boolean }> {
    return this.request('/api/widget/agent-info/');
  }
}
